/**
 * IELTS V2 — Reading Simulation Player
 * Route: /resources/ielts/reading/simulation?variant=academic|general_training
 *
 * Full-screen dedicated Winhouse reading testing environment.
 * LEFT: passage (independent scroll). RIGHT: numbered questions.
 * Server-authoritative 60-minute timer. Autosave. Resume-safe.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, BookOpen, AlertTriangle, Clock, Save, CheckCircle2,
  ChevronRight, ChevronLeft, Play, ShieldCheck, ListChecks, Monitor,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });
const AUTOSAVE_DEBOUNCE_MS = 1200;

function fmt(sec) {
  const s = Math.max(0, Number(sec) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export default function IeltsReadingSimulationPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const variantParam = params.get('variant') || localStorage.getItem('ielts_variant') || 'academic';
  const [phase, setPhase] = useState('info');
  const [ack, setAck] = useState(false);
  const [state, setState] = useState(null);
  const [responses, setResponses] = useState({});
  const [currentSection, setCurrentSection] = useState(1);
  const [saving, setSaving] = useState('idle');
  const [remaining, setRemaining] = useState(0);
  const [submitError, setSubmitError] = useState(null);
  const [narrow, setNarrow] = useState(typeof window !== 'undefined' ? window.innerWidth < 900 : false);

  const saveTimer = useRef(null);
  const pendingRef = useRef({});

  useEffect(() => {
    const onR = () => setNarrow(window.innerWidth < 900);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);

  useEffect(() => {
    if (phase !== 'in_test') return;
    const before = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', before);
    return () => window.removeEventListener('beforeunload', before);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'in_test') return;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'in_test' || !state?.id) return;
    const t = setInterval(async () => {
      try {
        const r = await ax.get(`/api/ielts/v2/reading/simulation/state/${state.id}`);
        if (typeof r.data?.remaining_seconds === 'number') {
          setRemaining(r.data.remaining_seconds);
        }
      } catch { setSaving('offline'); }
    }, 20000);
    return () => clearInterval(t);
  }, [phase, state?.id]);

  useEffect(() => {
    if (phase !== 'in_test' || remaining > 0) return;
    const t = setTimeout(() => setPhase('confirm'), 300);
    return () => clearTimeout(t);
  }, [phase, remaining]);

  const queueSave = useCallback((patch) => {
    pendingRef.current = { ...pendingRef.current, ...patch };
    setSaving('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const payload = { responses: pendingRef.current, current_section: currentSection };
      pendingRef.current = {};
      try {
        const r = await ax.post(
          `/api/ielts/v2/reading/simulation/state/${state.id}/save-bulk`, payload);
        if (typeof r.data?.remaining_seconds === 'number') {
          setRemaining(r.data.remaining_seconds);
        }
        setSaving('saved');
      } catch { setSaving('offline'); }
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [currentSection, state?.id]);

  const startSimulation = useCallback(async () => {
    try {
      localStorage.setItem('ielts_variant', variantParam);
      const r = await ax.post('/api/ielts/v2/reading/simulation/start', { variant: variantParam });
      const a = r.data || {};
      const stateResp = await ax.get(`/api/ielts/v2/reading/simulation/state/${a.id}`);
      const full = stateResp.data || {};
      setState(full);
      setResponses(full.responses || {});
      setCurrentSection(full.current_section || 1);
      setRemaining(full.remaining_seconds || 0);
      setPhase('in_test');
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error('Please log in first.');
        nav('/login?redirect=/resources/ielts/reading/simulation');
        return;
      }
      toast.error(e?.response?.data?.detail || 'Unable to start the simulation.');
    }
  }, [nav, variantParam]);

  const setAnswer = (qid, value) => {
    setResponses((prev) => ({ ...prev, [qid]: value }));
    queueSave({ [qid]: value });
  };

  const goToSection = (n) => {
    setCurrentSection(n);
    queueSave({});
    setTimeout(() => {
      const el = document.getElementById('ielts-rsim-top');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 30);
  };

  const finalSubmit = async () => {
    setPhase('submitting');
    setSubmitError(null);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (Object.keys(pendingRef.current).length) {
        await ax.post(`/api/ielts/v2/reading/simulation/state/${state.id}/save-bulk`, {
          responses: pendingRef.current, current_section: currentSection,
        });
        pendingRef.current = {};
      }
      const r = await ax.post(`/api/ielts/v2/reading/simulation/state/${state.id}/complete`);
      if (r.data?.attempt_id) {
        setPhase('complete');
        let mockCtx = null;
        try { mockCtx = JSON.parse(localStorage.getItem('ielts_mock_context') || 'null'); } catch { mockCtx = null; }
        if (mockCtx?.mockId && mockCtx.section === 'reading') {
          nav(`/resources/ielts/mock/session/${mockCtx.mockId}`, { replace: true });
        } else {
          nav(`/resources/ielts/reading/results/${r.data.attempt_id}`, { replace: true });
        }
      } else { throw new Error('Missing attempt id'); }
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || 'We could not submit your test.');
      setPhase('in_test');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 text-slate-100 overflow-y-auto"
       data-testid="ielts-rsim-shell">
      {phase === 'info' && (
        <TestInfo onBegin={startSimulation} ack={ack} setAck={setAck}
          variant={variantParam} onExit={() => nav('/resources/ielts')} narrow={narrow} />
      )}
      {phase === 'in_test' && state && (
        <InTest state={state} responses={responses} setAnswer={setAnswer}
          currentSection={currentSection} goToSection={goToSection}
          remaining={remaining} saving={saving} narrow={narrow}
          onFinishClick={() => setPhase('confirm')} />
      )}
      {phase === 'confirm' && (
        <ConfirmSubmit responses={responses} totalQuestions={40}
          onCancel={() => setPhase('in_test')} onConfirm={finalSubmit} timeUp={remaining <= 0} />
      )}
      {phase === 'submitting' && (
        <Centered>
          <Loader2 className="h-8 w-8 animate-spin text-indigo-400 mb-4" />
          <div className="text-xl font-bold">Marking your test…</div>
          {submitError && <div className="mt-4 text-rose-400 text-sm">{submitError}</div>}
        </Centered>
      )}
      {phase === 'complete' && (
        <Centered>
          <CheckCircle2 className="h-10 w-10 text-emerald-400 mb-3" />
          <div className="text-xl font-bold">Redirecting to your results…</div>
        </Centered>
      )}
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center px-6"><div className="text-center max-w-md">{children}</div></div>;
}

function TestInfo({ onBegin, ack, setAck, variant, onExit, narrow }) {
  const label = variant === 'academic' ? 'IELTS Academic' : 'IELTS General Training';
  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-16">
      <div className="text-[11px] uppercase tracking-widest text-indigo-300 font-bold mb-3">
        Winhouse IELTS · Full Reading Simulation
      </div>
      <h1 className="text-3xl md:text-5xl font-black">
        {label} <span className="text-indigo-400">Reading</span> Simulation
      </h1>
      <p className="text-slate-300 mt-3 text-sm md:text-base">
        Dedicated Winhouse testing environment. Complete the test in one
        uninterrupted sitting for the most accurate practice band estimate.
      </p>

      {narrow && (
        <div className="mt-6 rounded-lg bg-slate-900/70 border border-slate-700/50 p-3 flex gap-3 text-xs text-slate-300"
          data-testid="ielts-rsim-desktop-advice">
          <Monitor className="h-4 w-4 mt-0.5 flex-shrink-0 text-indigo-300" />
          For the closest IELTS simulation experience, Winhouse recommends
          using a desktop or laptop — you can still take the test here.
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Info icon={<ListChecks className="h-5 w-5" />} title="Test format" body="3 sections · 40 questions in total. Read the passage on the left; answer the numbered questions on the right." />
        <Info icon={<Clock className="h-5 w-5" />} title="Timing" body="60 minutes total, server-controlled. There is NO extra time to transfer answers — write directly into the boxes." />
        <Info icon={<BookOpen className="h-5 w-5" />} title="Instructions" body="Read the section instructions carefully. Some questions have word limits (e.g. NO MORE THAN TWO WORDS)." />
        <Info icon={<ShieldCheck className="h-5 w-5" />} title="Test integrity" body="No answers, explanations or evidence are shown during the test. Your work is autosaved and can be resumed if you disconnect." />
      </div>

      <div className="mt-6 rounded-lg bg-amber-950/40 border border-amber-700/40 p-4 text-sm text-amber-200 flex gap-3">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>Once you begin, the timer starts and cannot be stopped. Closing
        the tab will preserve your progress but the timer will continue to run.</div>
      </div>

      <label className="mt-6 flex items-start gap-3 text-sm cursor-pointer select-none">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
          data-testid="ielts-rsim-ack" className="mt-1 h-4 w-4 accent-indigo-500" />
        <span>
          I understand this is a Winhouse Estimated IELTS Practice Band and not
          an official IELTS score. Winhouse Tutors is not affiliated with IDP,
          British Council or Cambridge Assessment English.
        </span>
      </label>

      <div className="mt-8 flex items-center gap-3">
        <button onClick={onBegin} disabled={!ack}
          data-testid="ielts-rsim-begin"
          className="px-6 py-3 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-40 text-white font-bold inline-flex items-center gap-2 transition">
          <Play className="h-4 w-4" /> Begin Reading Simulation
        </button>
        <button onClick={onExit} className="px-4 py-2 text-slate-400 hover:text-slate-100 text-sm">
          Return to IELTS Hub
        </button>
      </div>
    </div>
  );
}

function Info({ icon, title, body }) {
  return (
    <div className="rounded-xl bg-slate-900/60 border border-slate-700/40 p-4">
      <div className="flex items-center gap-2 text-indigo-300 font-bold text-sm">{icon}{title}</div>
      <div className="mt-2 text-sm text-slate-300 leading-relaxed">{body}</div>
    </div>
  );
}

function InTest({ state, responses, setAnswer, currentSection, goToSection, remaining, saving, narrow, onFinishClick }) {
  const sections = state.sections || [];
  const section = sections.find((s) => s.section_number === currentSection) || sections[0];
  const answered = useMemo(() => {
    return sections.reduce((acc, s) => {
      const c = (s.questions || []).filter((q) => responses[q.id] !== undefined && responses[q.id] !== '').length;
      acc[s.section_number] = c; return acc;
    }, {});
  }, [sections, responses]);
  if (!section) return <Centered><Loader2 className="h-6 w-6 animate-spin" /></Centered>;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur border-b border-slate-700/40 px-4 md:px-8 py-3 flex items-center gap-3 md:gap-6">
        <div className="text-[10px] md:text-xs font-black tracking-widest text-indigo-300">WINHOUSE · IELTS READING</div>
        <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-300">
          <span className="text-slate-500">Section {currentSection} / 3</span>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-xs text-slate-300">
            <Save className="h-3.5 w-3.5" />
            <span data-testid="ielts-rsim-save-chip"
              className={saving === 'offline' ? 'text-rose-400' : (saving === 'saving' ? 'text-amber-300' : 'text-emerald-300')}>
              {saving === 'saving' ? 'Saving…' : saving === 'offline' ? 'Reconnecting…' : 'Saved'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-slate-800 px-3 py-1.5 text-sm font-bold tabular-nums"
             data-testid="ielts-rsim-timer">
            <Clock className="h-3.5 w-3.5 text-indigo-300" />
            {fmt(remaining)}
          </div>
        </div>
      </div>

      <div id="ielts-rsim-top" className="flex-1 flex flex-col lg:flex-row max-w-[1600px] w-full mx-auto">
        {/* Passage */}
        <div className="lg:w-1/2 lg:border-r lg:border-slate-700/40 px-4 md:px-6 py-5 lg:py-6"
          data-testid={`ielts-rsim-passage-${section.section_number}`}>
          <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold">Section {section.section_number} of 3</div>
          <div className="text-lg md:text-2xl font-black">{section.title}</div>
          {section.instructions && (
            <div className="mt-2 text-[11px] uppercase tracking-widest text-amber-300 font-bold">
              {section.instructions}
            </div>
          )}
          <div className="mt-3 rounded-lg bg-slate-900/60 border border-slate-700/40 p-4 max-h-[70vh] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-slate-200"
            data-testid={`ielts-rsim-passage-body-${section.section_number}`}>
            {section.passage}
          </div>
        </div>

        {/* Questions */}
        <div className="lg:w-1/2 px-4 md:px-6 py-5 lg:py-6">
          <div className="flex items-baseline justify-between">
            <div className="text-xs text-slate-400">
              <b className="text-slate-100">Section {section.section_number}</b> · questions
            </div>
            <div className="text-[11px] text-slate-400">
              <span className="tabular-nums font-bold text-slate-100">{answered[section.section_number] || 0}</span>
              {' / '}{section.questions.length} answered
            </div>
          </div>
          <div className="mt-4 space-y-3 lg:max-h-[70vh] lg:overflow-y-auto pr-2">
            {section.questions.map((q) => (
              <QuestionCard key={q.id} q={q} value={responses[q.id]}
                onChange={(v) => setAnswer(q.id, v)} />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="max-w-[1600px] mx-auto w-full px-4 md:px-6 pb-4">
        <div className="rounded-xl bg-slate-900/90 border border-slate-700/40 p-3 flex items-center gap-3 flex-wrap">
          <button onClick={() => goToSection(Math.max(1, currentSection - 1))}
            disabled={currentSection === 1}
            data-testid="ielts-rsim-prev-section"
            className="px-3 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 disabled:opacity-30 text-xs font-bold inline-flex items-center gap-1">
            <ChevronLeft className="h-3.5 w-3.5" /> Previous Section
          </button>
          <div className="flex items-center gap-1.5">
            {sections.map((s) => (
              <button key={s.section_number} onClick={() => goToSection(s.section_number)}
                data-testid={`ielts-rsim-goto-section-${s.section_number}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                  s.section_number === currentSection
                    ? 'bg-indigo-500 border-indigo-500 text-white'
                    : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-indigo-400'}`}>
                Section {s.section_number} <span className="text-[10px] opacity-70">· {answered[s.section_number] || 0}/{s.questions.length}</span>
              </button>
            ))}
          </div>
          {currentSection < 3 ? (
            <button onClick={() => goToSection(currentSection + 1)}
              data-testid="ielts-rsim-next-section"
              className="ml-auto px-4 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 text-xs font-bold inline-flex items-center gap-1">
              Next Section <ChevronRight className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button onClick={onFinishClick}
              data-testid="ielts-rsim-finish"
              className="ml-auto px-5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-black inline-flex items-center gap-1.5">
              Finish &amp; Submit <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function QuestionCard({ q, value, onChange }) {
  return (
    <div className="rounded-lg bg-slate-900/40 border border-slate-700/40 p-3"
      data-testid={`ielts-rsim-q-${q.id}`}>
      <div className="text-sm font-semibold text-slate-100">
        <span className="text-indigo-300 mr-2">{q.number}.</span> {q.prompt}
      </div>
      {q.options ? (
        <div className="mt-2 space-y-1.5">
          {q.options.map((opt) => {
            const letter = opt.split('.')[0].trim();
            const isBool = /^(TRUE|FALSE|NOT GIVEN|YES|NO)$/i.test(letter);
            const val = isBool ? letter.toUpperCase() : letter;
            return (
              <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer hover:text-white text-slate-300">
                <input type="radio" name={q.id} value={val}
                  checked={value === val}
                  onChange={() => onChange(val)}
                  data-testid={`ielts-rsim-q-${q.id}-opt-${val}`}
                  className="accent-indigo-500" />
                {opt}
              </label>
            );
          })}
        </div>
      ) : (
        <input type="text"
          className="mt-2 w-full bg-slate-950/50 border border-slate-700 focus:border-indigo-400 rounded px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none"
          placeholder={q.word_limit ? `Type your answer (max ${q.word_limit} words)` : 'Your answer'}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`ielts-rsim-q-${q.id}-input`} />
      )}
      {q.word_limit && (
        <div className="mt-1 text-[10px] text-slate-500">Word limit: {q.word_limit}</div>
      )}
    </div>
  );
}

function ConfirmSubmit({ responses, totalQuestions, onCancel, onConfirm, timeUp }) {
  const answered = Object.keys(responses).filter((k) => responses[k] !== undefined && responses[k] !== '').length;
  const unanswered = totalQuestions - answered;
  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="max-w-lg w-full rounded-2xl bg-slate-900 border border-slate-700 p-6 md:p-8">
        <div className="text-xs uppercase tracking-widest text-indigo-300 font-bold">Final submission</div>
        <h2 className="text-2xl font-black mt-1">Submit your Reading test?</h2>
        {timeUp && (
          <div className="mt-3 text-sm text-amber-300 flex items-center gap-2">
            <Clock className="h-4 w-4" /> Time has ended — please submit to see your results.
          </div>
        )}
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex items-baseline gap-2">
            <span className="text-slate-400">Answered:</span>
            <span className="font-bold text-emerald-300 tabular-nums">{answered} / {totalQuestions}</span>
          </div>
          {unanswered > 0 && (
            <div className="flex items-baseline gap-2">
              <span className="text-slate-400">Unanswered:</span>
              <span className="font-bold text-amber-300 tabular-nums">{unanswered}</span>
            </div>
          )}
        </div>
        <div className="mt-4 text-xs text-slate-400 leading-relaxed">
          Once submitted, your test will be marked instantly. Your attempt is
          permanently preserved.
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button onClick={onCancel} data-testid="ielts-rsim-confirm-cancel"
            className="px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-sm text-slate-100">
            Back to test
          </button>
          <button onClick={onConfirm} data-testid="ielts-rsim-confirm-submit"
            className="ml-auto px-5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-black">
            Submit test
          </button>
        </div>
      </div>
    </div>
  );
}
